import React, { useState, useEffect } from 'react';
import gql from 'graphql-tag';
import { Mutation } from 'react-apollo';
import swal from 'sweetalert';
import { isNull, omit, snakeCase } from 'lodash';
import { toast } from 'react-toastify';
import { useStoreState } from 'easy-peasy';

import client from '../../../utils/apolloClient';
import { ErrorMessage } from '../../../components/elements';
import Layout from '../../../components/global/Layout';
import PlaceSelection from '../../../components/PlaceSelection';
import Form from './Form';
import { getServiceType, reverseDayEstimateValueParser } from './helpers';
import { parseContactValidValue } from '../../../utils/helpers';

const createServiceTypeSettingMutation = gql`
  mutation createServiceTypeSetting($input: ServiceTypeSettingInput) {
    create_service_type_setting(input: $input) {
      service_type_setting_id
      error {
        description
      }
    }
  }
`;

const searchServiceSettingsQuery = gql`
  query searchSettings($input: SearchInput) {
    search_service_type_setting(input: $input) {
      service_type_setting_listing {
        service_type_setting_id
        place_id
        service_type
        status
        approval_status
        is_custom_service
      }
    }
  }
`;

// eslint-disable-next-line import/prefer-default-export
export const getServiceTypeSettings = ({ placeId }) =>
  new Promise((resolve, reject) => {
    client.clientPrivate
      .query({
        client: client.clientPrivate,
        query: searchServiceSettingsQuery,
        variables: {
          input: {
            filter: {
              service_type_setting_filter: {
                place_id: placeId,
              },
            },
          },
        },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        if (
          data.search_service_type_setting &&
          data.search_service_type_setting.service_type_setting_listing
        ) {
          resolve(
            data.search_service_type_setting.service_type_setting_listing,
          );
        } else {
          resolve([]);
        }
      })
      .catch(error => {
        console.log(error);
        reject(error);
      });
  });

const MenuTypeAdd = ({ history }) => {
  const { userId } = useStoreState(state => state.auth);
  const { defaultPlace } = useStoreState(state => state.menu);
  const [place, setPlace] = useState();
  const [placeSelection, setPlaceSelection] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([
    { value: 'custom', label: 'Create new one' },
  ]);

  const placeData = defaultPlace ?? place;
  const placeSelectionValue =
    defaultPlace !== null ? !!defaultPlace : placeSelection;

  useEffect(() => {
    const fetchData = async () => {
      const data = await getServiceType();
      const existingServiceTypes = await getServiceTypeSettings({
        placeId: placeData?.place_id,
      });
      const removeExistingNormalServices = arr => {
        const normalServices = [];
        existingServiceTypes.forEach(serviceType => {
          if (!serviceType?.is_custom_service) {
            normalServices.push(serviceType?.service_type);
          }
        });
        if (normalServices.length === 0) {
          return arr;
        }
        return arr.filter(ele1 => {
          const ns = normalServices.find(ele2 => ele2 === ele1.name);
          if (ns) {
            return false;
          }
          return true;
        });
      };

      const newData = removeExistingNormalServices(data).map(item => ({
        ...item,
        value: snakeCase(item.name),
        label: item.name,
      }));
      const custom = [{ value: 'custom', label: 'Create new one' }];
      if (placeData.claim_status === 'UNCLAIMED') {
        setServiceTypes(custom);
      } else {
        setServiceTypes(custom.concat(newData));
      }
    };
    if (placeData) {
      fetchData();
    }
  }, [placeData]);

  return (
    <Layout>
      {placeSelectionValue ? (
        <Mutation
          client={client.clientPrivate}
          mutation={createServiceTypeSettingMutation}
          onCompleted={({ create_service_type_setting }) => {
            if (!isNull(create_service_type_setting.error)) {
              create_service_type_setting.error.map(item =>
                toast.error(item.description),
              );
            } else {
              swal(
                'Great!',
                'Service settings configured successfully!',
                'success',
              ).then(() => {
                history.goBack();
              });
            }
          }}
        >
          {(create_service_type_setting, { loading, error }) => (
            <>
              {error && <ErrorMessage message={error.message} />}
              <Form
                isAdd
                userId={userId}
                place={placeData}
                loading={loading}
                serviceTypes={serviceTypes}
                onSubmit={inputs => {
                  const input = omit(inputs, [
                    'menus',
                    'slug',
                    'step',
                    'day_estimate',
                    'primaryEmail',
                    'primaryMobile',
                    'inStore',
                    'curbSide',
                    'carPark',
                    'driveThru',
                    'isAdd',
                  ]);
                  const dayEstimate = reverseDayEstimateValueParser(
                    inputs.day_estimate,
                  );
                  const primaryContacts = [
                    {
                      type: 'email_primary',
                      value: inputs.primaryEmail,
                      display: true,
                      is_primary: true,
                    },
                    {
                      type: 'phone_primary',
                      value: inputs.primaryMobile,
                      display: true,
                      is_primary: true,
                    },
                  ];
                  let finalInput = {};
                  const temp = omit(
                    input,
                    'auto_confirm',
                    'auto_ready',
                    'delivery_channel',
                    'time_in_mins',
                    'is_auto_cancel',
                    'delivery_integration',
                    'random_id',
                  );
                  finalInput = {
                    ...temp,
                    day_estimate: dayEstimate,
                    contact: parseContactValidValue(primaryContacts),
                    publish_service_type: false,
                    allow_order_cancellation: input.allow_order_cancellation,
                  };
                  create_service_type_setting({
                    variables: {
                      input: finalInput,
                    },
                  });
                }}
              />
            </>
          )}
        </Mutation>
      ) : (
        <PlaceSelection
          heading="Select Place to add service setting"
          claimStatus={null}
          handleSelect={value => {
            setPlace(value);
            setPlaceSelection(true);
          }}
        />
      )}
    </Layout>
  );
};

export default MenuTypeAdd;
